/**
 * Fase 2A · P0-1 · EmpleadoMyProfile
 * Perfil operativo del empleado. Edita nombre, avatar, contraseña.
 * Backend: PUT /api/users/me · PUT /api/users/me/password (cualquier rol logueado).
 */
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Skeleton } from "../../components/ui/skeleton";
import PasswordInput from "../components/PasswordInput";
import { toast } from "sonner";
import {
  User as UserIcon, Lock, FloppyDisk, EnvelopeSimple, Buildings, Sparkle,
} from "@phosphor-icons/react";

export default function EmpleadoMyProfile() {
  const { user, org, refresh } = useAuth();
  const [profile, setProfile] = useState(null);
  const [name, setName] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);

  const [currentPass, setCurrentPass] = useState("");
  const [newPass, setNewPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [savingPass, setSavingPass] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setProfile(data);
        setName(data?.name || "");
        setAvatarUrl(data?.avatar_url || "");
      } catch {
        setProfile(user || {});
        setName(user?.name || "");
      }
    })();
  }, [user?.id]);

  const saveProfile = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("El nombre no puede estar vacío"); return; }
    setSavingProfile(true);
    try {
      await api.put("/users/me", {
        name: name.trim(),
        avatar_url: avatarUrl.trim() || null,
      });
      toast.success("Perfil actualizado");
      if (typeof refresh === "function") await refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se pudo guardar el perfil");
    } finally { setSavingProfile(false); }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (newPass.length < 6) { toast.error("Contraseña mínima de 6 caracteres"); return; }
    if (newPass !== confirmPass) { toast.error("Las contraseñas no coinciden"); return; }
    setSavingPass(true);
    try {
      await api.put("/users/me/password", {
        current_password: currentPass,
        new_password: newPass,
      });
      toast.success("Contraseña actualizada · vuelve a iniciar sesión en otros dispositivos");
      setCurrentPass(""); setNewPass(""); setConfirmPass("");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se pudo cambiar la contraseña");
    } finally { setSavingPass(false); }
  };

  if (!profile) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 rounded-sm" />
        <Skeleton className="h-64 rounded-sm" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl" data-testid="empleado-perfil">
      <header>
        <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ag-gold)] font-semibold">
          Mi perfil operativo
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-black tracking-tighter mt-1">
          Datos personales
        </h1>
        <p className="text-sm text-[var(--ag-text-dim)] mt-2">
          Cómo apareces al equipo y a los clientes del centro.
        </p>
      </header>

      {/* Identidad · read-only */}
      <section className="ag-card p-5 space-y-3" data-testid="empleado-perfil-identidad">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ag-text-dim)] flex items-center gap-2">
          <Sparkle weight="fill" /> Identidad
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-wider text-[var(--ag-text-dim)]">Email</Label>
            <div className="flex items-center gap-2 mt-1 px-3 py-2 bg-[var(--ag-midnight)]/60 border border-[var(--ag-border)] rounded text-sm text-[var(--ag-text)]">
              <EnvelopeSimple size={13} weight="bold" className="text-[var(--ag-text-dim)]" />
              <span data-testid="empleado-perfil-email">{profile.email}</span>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[var(--ag-text-dim)]">Centro</Label>
            <div className="flex items-center gap-2 mt-1 px-3 py-2 bg-[var(--ag-midnight)]/60 border border-[var(--ag-border)] rounded text-sm text-[var(--ag-text)]">
              <Buildings size={13} weight="duotone" className="text-[var(--ag-gold)]" />
              <span className="truncate" data-testid="empleado-perfil-org">{org?.name || "—"}</span>
            </div>
          </div>
        </div>
        <div className="text-[11px] text-[var(--ag-text-dim)]">
          Para cambiar email o rol contacta con el titular del centro.
        </div>
      </section>

      {/* Editar nombre + avatar */}
      <form onSubmit={saveProfile} className="ag-card p-5 space-y-4" data-testid="empleado-perfil-form">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ag-text-dim)] flex items-center gap-2">
          <UserIcon weight="duotone" /> Datos públicos
        </div>
        <div>
          <Label htmlFor="empleado-name" className="text-xs uppercase tracking-wider text-[var(--ag-text-dim)]">
            Nombre visible
          </Label>
          <Input
            id="empleado-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ej. Ana Operaria"
            required
            maxLength={80}
            className="mt-1"
            data-testid="empleado-perfil-name"
          />
        </div>
        <div>
          <Label htmlFor="empleado-avatar" className="text-xs uppercase tracking-wider text-[var(--ag-text-dim)]">
            URL de tu foto (opcional)
          </Label>
          <Input
            id="empleado-avatar"
            type="url"
            value={avatarUrl}
            onChange={(e) => setAvatarUrl(e.target.value)}
            placeholder="https://…"
            className="mt-1"
            data-testid="empleado-perfil-avatar"
          />
          <div className="text-[11px] text-[var(--ag-text-dim)] mt-1">
            Se muestra a tu equipo y a los clientes que reservan contigo.
          </div>
        </div>
        <div className="flex justify-end">
          <Button type="submit" disabled={savingProfile} className="ag-btn-gold" data-testid="empleado-perfil-save">
            <FloppyDisk size={14} className="mr-1.5" weight="duotone" />
            {savingProfile ? "Guardando…" : "Guardar cambios"}
          </Button>
        </div>
      </form>

      {/* Cambiar contraseña */}
      <form onSubmit={savePassword} className="ag-card p-5 space-y-4" data-testid="empleado-perfil-password-form">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ag-text-dim)] flex items-center gap-2">
          <Lock weight="duotone" /> Seguridad
        </div>
        <div>
          <Label htmlFor="empleado-currpass" className="text-xs uppercase tracking-wider text-[var(--ag-text-dim)]">
            Contraseña actual
          </Label>
          <PasswordInput
            id="empleado-currpass"
            value={currentPass}
            onChange={(e) => setCurrentPass(e.target.value)}
            required
            className="mt-1"
            testId="empleado-perfil-currpass"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="empleado-newpass" className="text-xs uppercase tracking-wider text-[var(--ag-text-dim)]">
              Nueva contraseña
            </Label>
            <PasswordInput
              id="empleado-newpass"
              value={newPass}
              onChange={(e) => setNewPass(e.target.value)}
              required
              minLength={6}
              className="mt-1"
              testId="empleado-perfil-newpass"
            />
          </div>
          <div>
            <Label htmlFor="empleado-confpass" className="text-xs uppercase tracking-wider text-[var(--ag-text-dim)]">
              Repetir nueva
            </Label>
            <PasswordInput
              id="empleado-confpass"
              value={confirmPass}
              onChange={(e) => setConfirmPass(e.target.value)}
              required
              minLength={6}
              className="mt-1"
              testId="empleado-perfil-confpass"
            />
          </div>
        </div>
        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={savingPass || !currentPass || !newPass || !confirmPass}
            className="ag-btn-gold"
            data-testid="empleado-perfil-savepass"
          >
            <Lock size={14} className="mr-1.5" weight="duotone" />
            {savingPass ? "Cambiando…" : "Actualizar contraseña"}
          </Button>
        </div>
      </form>
    </div>
  );
}
